import { Box, SxProps, useTheme } from "@mui/material";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { ILineGraphDataset } from "../models";

ChartJS.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController
);

interface IProps {
  datasets: ILineGraphDataset[];
  annotations?: { rowGuid: string; text: string }[];
  xAxisLabels?: string[];
  sx?: SxProps;
  minYValue?: number;
  maxYValue?: number;
  stacked?: boolean;
  drawPoints?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
}

const Chart = (props: IProps) => {
  const theme = useTheme();

  const iconImage = new Image();
  iconImage.src = "/svg/sticky-note.svg";

  let yValues = props.datasets.flatMap((d) => d.data.filter((p) => p.y !== null).map((p) => p.y!));

  const maxYValue = Math.max(...yValues);
  const minYValue = Math.min(...yValues);
  const calculatedMaxYValue = maxYValue + (maxYValue - minYValue) * 0.1;

  const dataRange = maxYValue - minYValue;
  const shouldUseZeroAsMin = minYValue >= 0 && minYValue < 50 && dataRange / maxYValue > 0.2;

  const calculatedMinYValue = shouldUseZeroAsMin ? 0 : minYValue - (maxYValue - minYValue) * 0.1;

  const tooltipLine = {
    id: "tooltipLine",
    beforeTooltipDraw: (chart: any) => {
      if (chart.tooltip?._active?.length) {
        let x = chart.tooltip._active[0].element.x;
        let ctx = chart.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, chart.chartArea.top);
        ctx.lineTo(x, chart.chartArea.bottom);
        ctx.strokeStyle = theme.palette.grey[300];
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  const iconSize = 14;

  const noteIcon = {
    id: "noteIcon",
    afterDraw: (chart: any) => {
      if (!iconImage) return; // Don't draw if image isn't loaded yet

      const {
        ctx,
        data,
        chartArea: { bottom },
        scales: { xAxis },
      } = chart;

      ctx.save();

      // Iterate through datasets to draw icons for each label
      data.datasets.forEach((dataset: any) => {
        dataset.data.forEach((point: any, index: number) => {
          if (point.hasAnnotation) {
            const xPos = xAxis.getPixelForValue(index);
            const yPos = bottom - 30;

            ctx.drawImage(iconImage, xPos - iconSize / 2, yPos, iconSize, iconSize);
          }
        });
      });

      ctx.restore();
    },
  };

  function sortDatasetsByType(a: ILineGraphDataset, b: ILineGraphDataset) {
    if (a.displayAsBar && !b.displayAsBar) return 1;
    if (!a.displayAsBar && b.displayAsBar) return -1;

    return a.sortOrder.localeCompare(b.sortOrder);
  }

  const sortedDatasets = [...(props.datasets || [])].sort(sortDatasetsByType);

  // Create a unique key for the chart based on dataset types to force re-render when types change
  const chartKey = sortedDatasets.map((d) => (d.displayAsBar ? "bar" : "line")).join("_");

  function getOptions(): ChartOptions<"line"> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: iconSize / 2,
          right: iconSize / 2,
        },
      },
      interaction: {
        mode: "index",
        axis: "x",
        intersect: false,
      },
      elements: {
        point: {
          borderWidth: 0,
          backgroundColor: "rgba(0,0,0,0)",
        },
      },
      plugins: {
        legend: {
          display: false,
          position: "top" as const,
        },
        tooltip: {
          enabled: false,
          position: "nearest",
          itemSort: (a: any, b: any) => {
            const datasetA = sortedDatasets[a.datasetIndex];
            const datasetB = sortedDatasets[b.datasetIndex];

            // Chart sorts to display lines over bars, but tooltip should sort its items by dataset sort order
            return (datasetA.sortOrder || "").localeCompare(datasetB.sortOrder || "");
          },
          external: function (context: any) {
            // Tooltip Element
            let tooltipEl = document.getElementById("chartjs-tooltip");

            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.style.background = theme.palette.common.white;
              tooltipEl.style.borderRadius = "8px";
              tooltipEl.style.color = theme.palette.text.primary;
              tooltipEl.style.border = `1px solid ${theme.palette.grey[200]}`;
              tooltipEl.style.opacity = "1";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.position = "absolute";
              tooltipEl.style.transition = "all .1s ease";
              tooltipEl.style.minWidth = "150px";
              tooltipEl.style.padding = "20px";
              tooltipEl.style.fontFamily = String(theme.typography.fontFamily);
              tooltipEl.style.fontSize = `${theme.typography.fontSize}px`;
              tooltipEl.style.zIndex = "999";
              document.body.appendChild(tooltipEl);
            }

            const tooltipModel = context.tooltip; //https://www.chartjs.org/docs/latest/configuration/tooltip.html#tooltip-model
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            // Set caret Position
            tooltipEl.classList.remove("above", "below", "no-transform");
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add("no-transform");
            }

            // Set Text
            if (tooltipModel.body) {
              const titleLines = tooltipModel.title || [];
              const bodyLines = tooltipModel.body.map((b: any) => b.lines);

              let innerHtml = "<div>";

              // Title
              titleLines.forEach((title: string) => {
                innerHtml += `<div style="margin-bottom: 8px;"><strong>${title}</strong></div>`;
              });

              // Body
              bodyLines.forEach((body: any, i: number) => {
                const colors = tooltipModel.labelColors[i];
                const style = `background:${colors.backgroundColor}; border-color:${colors.borderColor}; border-width: 2px; display: inline-block; height: 8px; width: 8px; border-radius: 50%; margin-right: 8px;`;
                const span = `<span style="${style}"></span>`;
                innerHtml += `<div style="display: flex; align-items: center; margin-bottom: 4px;">${span}${body}</div>`;
              });

              // Check for annotation
              const dataPoint = tooltipModel.dataPoints?.[0];
              if (dataPoint) {
                const dataset = context.chart.data.datasets[dataPoint.datasetIndex];
                const point = dataset.data[dataPoint.dataIndex];

                if (point?.rowGuid) {
                  const annotation = props.annotations?.find((a) => a.rowGuid === point.rowGuid);

                  if (annotation) {
                    innerHtml += `<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid ${theme.palette.grey[200]};"><strong>Note:</strong></div>`;
                    innerHtml += `<div style="margin-top: 4px; max-width: 300px; word-wrap: break-word;">${annotation.text}</div>`;
                  }
                }
              }

              innerHtml += "</div>";

              tooltipEl.innerHTML = innerHtml;
            }

            const position = context.chart.canvas.getBoundingClientRect();

            const tooltipX = position.left + window.pageXOffset + tooltipModel.caretX;
            const tooltipY = position.top + window.pageYOffset + tooltipModel.caretY;
            tooltipEl.style.left = tooltipX + "px";
            tooltipEl.style.top = tooltipY + "px";

            const { horizontal, vertical, offsetX } = getTooltipPosition(tooltipEl, tooltipX, tooltipY);

            tooltipEl.style.transform = `translate(${horizontal}, ${vertical})`;

            tooltipEl.style.opacity = "1";
            tooltipEl.style.left = tooltipX + offsetX + "px";
            tooltipEl.style.top = tooltipY + "px";
          },
        },
      },
      scales: {
        xAxis: {
          display: props.showXAxis ? true : false,
        },
        yAxis: {
          display: props.showYAxis ? true : false,
          stacked: props.stacked,
          min: yValues.length > 1 ? props.minYValue ?? calculatedMinYValue : undefined,
          max: yValues.length > 1 ? props.maxYValue ?? calculatedMaxYValue : undefined,
        },
      },
    };
  }

  function getData() {
    return {
      datasets: sortedDatasets.flatMap((d) => [
        {
          label: d.title,
          type: d.displayAsBar ? ("bar" as any) : ("line" as any),
          data: d.data,
          backgroundColor: d.color,
          borderColor: d.color,
          tension: 0.3,
          pointRadius: props.drawPoints ? 3 : 0,
          pointBackgroundColor: d.color,
          borderDash: d.dotted ? [3, 3] : [0],
        },
      ]),
    };
  }

  return (
    <Box sx={{ ...props.sx, overflow: "hidden" }}>
      <Line key={chartKey} options={getOptions()} plugins={[tooltipLine, noteIcon]} data={getData()} />
    </Box>
  );
};

const getTooltipPosition = (
  tooltipEl: HTMLElement,
  tooltipX: number,
  tooltipY: number
): { horizontal: string; vertical: string; offsetX: number } => {
  const SPACING = 20; // 20px space between tooltip and position

  const tooltipRect = tooltipEl.getBoundingClientRect();
  const tooltipWidth = tooltipRect.width;
  const tooltipHeight = tooltipRect.height;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Determine horizontal positioning
  let horizontalTransform = "-100%"; // Default: position to the left
  let offsetX = -SPACING;
  const spaceOnLeft = tooltipX;
  const spaceOnRight = viewportWidth - tooltipX;

  // If tooltip would go off left edge, flip to right
  if (spaceOnLeft < tooltipWidth + SPACING && spaceOnRight > tooltipWidth + SPACING) {
    horizontalTransform = "0%";
    offsetX = SPACING;
  }

  // Determine vertical positioning
  let verticalTransform = "0%"; // Default: position below
  const spaceAbove = tooltipY;
  const spaceBelow = viewportHeight - tooltipY;

  // If tooltip would go off bottom edge, align to bottom
  if (spaceBelow < tooltipHeight / 2 && spaceAbove > tooltipHeight) {
    verticalTransform = "-100%";
  }

  return {
    horizontal: horizontalTransform,
    vertical: verticalTransform,
    offsetX,
  };
};

export { Chart };
